import { useCallback } from 'react';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_APP_VIEW_SEPARATION } from '@/consts/featureFlags';
import { useToolTabsEnabled } from '@/hooks/useToolTabsEnabled';
import { goToRecipe, goToTool, goToWorkflowCanvas } from '@/utils/recipes';
import type { DashboardWorkflow } from '@/types/workflows';

type OpenableWorkflow = Pick<DashboardWorkflow, 'isApp' | 'toolId' | 'sharedAsWorkflow'> & { id: string | null };

/** Whether opening this row lands on the tool page. The badge is drawn from the same facts, so it
 *  always names where the click goes. */
export const opensTool = (
  workflow: Pick<OpenableWorkflow, 'isApp' | 'toolId'> | undefined,
  isToolViewEnabled: boolean | undefined,
): boolean => !!isToolViewEnabled && !!workflow?.isApp && !!workflow?.toolId;

export const useGoToWorkflow = () => {
  const { value: isToolViewEnabled } = useFeatureFlag(FF_APP_VIEW_SEPARATION);
  const areTabsEnabled = useToolTabsEnabled();
  return useCallback(
    (workflow: OpenableWorkflow, openInNewTab = false) => {
      // A row that is a tool opens the tool, whoever is asking — that is what its label promises.
      // The tool page carries "Go to workflow" for the way back.
      if (opensTool(workflow, isToolViewEnabled)) {
        goToTool(workflow.toolId ?? null, openInNewTab);
        return;
      }
      // Granted the workflow itself, so the canvas is the destination, not the tool page /flow would
      // redirect them to. `?view=workflow` is that redirect's opt-out (WEA-5088). Not free: it pins
      // Workflow mode over the app view a viewer of a published workflow gets by default. Behind the
      // flag, because off it the recipient still reaches each kind from its own section.
      if (workflow.sharedAsWorkflow && areTabsEnabled) {
        goToWorkflowCanvas(workflow.id, openInNewTab);
        return;
      }
      goToRecipe(workflow.id, openInNewTab);
    },
    [areTabsEnabled, isToolViewEnabled],
  );
};
